// src/pages/admin/AdminMessagesPage.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  X,
  Filter,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Reply,
  MoreVertical,
  RefreshCw,
  Loader2,
  Calendar,
  User,
  Phone,
  Tag,
  Flag,
  Star,
  AlertCircle,
  Check,
  Plus,
  Minus,
  FileText,
  Download,
  Trash2,
  Edit,
  Save,
  Send,
  ArrowLeft,
  ArrowRight,
  MessageSquare,
  Users,
  BarChart3,
  Activity,
  Zap,
  Shield,
  Globe,
  Clock as ClockIcon,
  MailOpen,
  MailX,
  MailCheck,
  MailQuestion,
  MailWarning,
  MailPlus,
  MailMinus,
  MailSearch,
  Heart,
  Mail,
  MailCheck as MailConfirm,
  MailX as MailCancel,
  MailPlus as MailAdd,
  MailMinus as MailRemove,
  Heart as MailFavorite,
  Ban ,
  MailCheck as MailVerified,
  MailX as MailUnverified
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/AdminMessages.module.css';

// Import message selectors and actions
import {
  getAllMessages,
  getMessageById,
  updateMessage,
  sendReply,
  getUrgentMessages,
  getOverdueMessages,
  getEscalatedMessages,
  getMessageStatistics,
  getMessageAnalytics,
  resendNotification,
  resetMessageState,
  clearMessageErrors,
  setMessageFilters,
  clearMessageFilters,
  setMessagePagination,
  setSelectedMessage,
  setActiveTab,
  updateLocalMessage,
  selectMessages,
  selectCurrentMessage,
  selectUrgentMessages,
  selectOverdueMessages,
  selectEscalatedMessages,
  selectMessageStatistics,
  selectMessageAnalytics,
  selectMessagePagination,
  selectMessagesLoading,
  selectMessageLoading,
  selectUrgentLoading,
  selectOverdueLoading,
  selectEscalatedLoading,
  selectStatisticsLoading,
  selectMessageError,
  selectSubmitMessageError,
  selectUpdateMessageError,
  selectSubmitMessageSuccess,
  selectUpdateMessageSuccess,
  selectReplySuccess,
  selectSelectedMessageId,
  selectActiveTab,
  selectMessagesByStatus,
  selectMessagesByPriority,
  selectPendingMessages,
  selectUnreadMessages,
  selectResolvedMessages,
  selectOverdueCount,
  selectUrgentCount,
  selectEscalatedCount,
  selectTotalMessages
} from '../../store/slices/messageSlice';

// ============================================================
// MESSAGE STATUS CONFIGURATION
// ============================================================
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#F59E0B', bg: '#FEF3C7', icon: Clock, badge: 'warning' },
  read: { label: 'Read', color: '#3B82F6', bg: '#DBEAFE', icon: Eye, badge: 'info' },
  in_progress: { label: 'In Progress', color: '#8B5CF6', bg: '#EDE9FE', icon: RefreshCw, badge: 'info' },
  responded: { label: 'Responded', color: '#06B6D4', bg: '#CFFAFE', icon: Reply, badge: 'info' },
  resolved: { label: 'Resolved', color: '#10B981', bg: '#D1FAE5', icon: CheckCircle, badge: 'success' },
  closed: { label: 'Closed', color: '#6B7280', bg: '#F3F4F6', icon: XCircle, badge: 'secondary' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: '#6B7280', bg: '#F3F4F6', icon: MailMinus },
  medium: { label: 'Medium', color: '#3B82F6', bg: '#DBEAFE', icon: Mail },
  high: { label: 'High', color: '#F59E0B', bg: '#FEF3C7', icon: MailWarning },
  urgent: { label: 'Urgent', color: '#EF4444', bg: '#FEE2E2', icon: Ban }
};

const MESSAGE_TYPE_CONFIG = {
  general: { label: 'General', icon: Mail, color: '#6B7280' },
  service_inquiry: { label: 'Service Inquiry', icon: MailSearch, color: '#3B82F6' },
  booking_request: { label: 'Booking Request', icon: MailPlus, color: '#8B5CF6' },
  complaint: { label: 'Complaint', icon: MailWarning, color: '#EF4444' },
  feedback: { label: 'Feedback', icon: Heart, color: '#10B981' },
  partnership: { label: 'Partnership', icon: MailAdd, color: '#8B5CF6' }
};

// ============================================================
// HELPER COMPONENTS
// ============================================================

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span 
      className={styles.statusBadge}
      style={{ backgroundColor: config.bg, color: config.color }}
    >
      {config.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  const Icon = config.icon;
  return (
    <span 
      className={styles.priorityBadge}
      style={{ backgroundColor: config.bg, color: config.color }}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
};

const MessageTypeBadge = ({ type }) => {
  const config = MESSAGE_TYPE_CONFIG[type] || MESSAGE_TYPE_CONFIG.general;
  const Icon = config.icon;
  return (
    <span 
      className={styles.typeBadge}
      style={{ color: config.color }}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const AdminMessagesPage = () => {
  const dispatch = useDispatch();
  const { currentLanguage } = useLanguage();
  const { translatedText } = useTranslation();

  // ============================================================
  // REDUX SELECTORS
  // ============================================================
  const messages = useSelector(selectMessages) || [];
  const currentMessage = useSelector(selectCurrentMessage);
  const urgentMessages = useSelector(selectUrgentMessages) || [];
  const overdueMessages = useSelector(selectOverdueMessages) || [];
  const escalatedMessages = useSelector(selectEscalatedMessages) || [];
  const statistics = useSelector(selectMessageStatistics);
  const analytics = useSelector(selectMessageAnalytics);
  const pagination = useSelector(selectMessagePagination) || { page: 1, limit: 20, total: 0, pages: 0 };
  const loading = useSelector(selectMessagesLoading);
  const loadingMessage = useSelector(selectMessageLoading);
  const urgentLoading = useSelector(selectUrgentLoading);
  const overdueLoading = useSelector(selectOverdueLoading);
  const escalatedLoading = useSelector(selectEscalatedLoading);
  const statsLoading = useSelector(selectStatisticsLoading);
  const error = useSelector(selectMessageError);
  const submitError = useSelector(selectSubmitMessageError);
  const updateError = useSelector(selectUpdateMessageError);
  const submitSuccess = useSelector(selectSubmitMessageSuccess);
  const updateSuccess = useSelector(selectUpdateMessageSuccess);
  const replySuccess = useSelector(selectReplySuccess);
  const selectedMessageId = useSelector(selectSelectedMessageId);
  const activeTab = useSelector(selectActiveTab);

  // Derived counts
  const pendingMessages = useSelector(selectPendingMessages) || [];
  const unreadMessages = useSelector(selectUnreadMessages) || [];
  const resolvedMessages = useSelector(selectResolvedMessages) || [];
  const overdueCount = useSelector(selectOverdueCount) || 0;
  const urgentCount = useSelector(selectUrgentCount) || 0;
  const escalatedCount = useSelector(selectEscalatedCount) || 0;
  const totalMessages = useSelector(selectTotalMessages) || 0;

  // ============================================================
  // LOCAL STATE
  // ============================================================
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedMessage, setSelectedMessageState] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showStats, setShowStats] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const searchInputRef = useRef(null);
  const replyTextareaRef = useRef(null);

  // ============================================================
  // TRANSLATIONS
  // ============================================================
  const t = (key) => translatedText(key) || key;

  // ============================================================
  // LOAD MESSAGES
  // ============================================================
  const loadMessages = useCallback(async (params = {}) => {
    try {
      const searchParams = {
        page: params.page || currentPage,
        limit: params.limit || itemsPerPage,
        sortBy: params.sortBy || sortField,
        sortOrder: params.sortOrder || sortDirection,
        ...params
      };

      // Add filters
      if (selectedStatus !== 'all') searchParams.status = selectedStatus;
      if (selectedPriority !== 'all') searchParams.priority = selectedPriority;
      if (selectedType !== 'all') searchParams.messageType = selectedType;
      if (searchQuery.trim()) searchParams.search = searchQuery.trim();

      console.log('📩 Loading messages with params:', searchParams);
      const result = await dispatch(getAllMessages(searchParams)).unwrap();
      console.log('✅ Messages loaded:', result.data.items?.length || 0);
      return result;
    } catch (error) {
      console.error('❌ Failed to load messages:', error);
      return null;
    }
  }, [dispatch, currentPage, itemsPerPage, sortField, sortDirection, selectedStatus, selectedPriority, selectedType, searchQuery]);

  // ============================================================
  // LOAD STATISTICS
  // ============================================================
  const loadStatistics = useCallback(async () => {
    try {
      await dispatch(getMessageStatistics()).unwrap();
      await dispatch(getMessageAnalytics({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        endDate: new Date()
      })).unwrap();
    } catch (error) {
      console.error('❌ Failed to load statistics:', error);
    }
  }, [dispatch]);

  // ============================================================
  // INITIAL LOAD
  // ============================================================
  useEffect(() => {
    const init = async () => {
      await loadMessages({ page: 1 });
      await loadStatistics();
    };
    init();

    return () => {
      dispatch(resetMessageState());
    };
  }, []); // Empty dependency array - runs once on mount

  // ============================================================
  // EFFECTS FOR FILTER CHANGES
  // ============================================================
  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        loadMessages({ page: 1 });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedStatus, selectedPriority, selectedType, searchQuery, sortField, sortDirection]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setCurrentPage(newPage);
      loadMessages({ page: newPage });
    }
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
    loadMessages({ page: 1, limit: newLimit });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    loadMessages({ page: 1, sortBy: field, sortOrder: sortDirection === 'asc' ? 'desc' : 'asc' });
  };

  const handleMessageSelect = async (message) => {
    try {
      const result = await dispatch(getMessageById(message._id)).unwrap();
      setSelectedMessageState(result.data);
      setShowDetailPanel(true);
      dispatch(setSelectedMessage(message._id));
    } catch (error) {
      console.error('❌ Failed to load message details:', error);
    }
  };

  const handleReplySubmit = async () => {
    if (!replyContent.trim() || !selectedMessage) return;

    try {
      await dispatch(sendReply({
        id: selectedMessage._id,
        content: replyContent,
        sentBy: 'admin'
      })).unwrap();

      setReplyContent('');
      setShowReplyModal(false);
      
      // Refresh message details
      const result = await dispatch(getMessageById(selectedMessage._id)).unwrap();
      setSelectedMessageState(result.data);
      
      // Refresh list
      await loadMessages({ page: currentPage });
    } catch (error) {
      console.error('❌ Failed to send reply:', error);
    }
  };

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      await dispatch(updateMessage({
        id: messageId,
        data: { status: newStatus }
      })).unwrap();

      // Update local message
      dispatch(updateLocalMessage({
        messageId,
        updateData: { status: newStatus }
      }));

      // Refresh details if selected
      if (selectedMessage && selectedMessage._id === messageId) {
        const result = await dispatch(getMessageById(messageId)).unwrap();
        setSelectedMessageState(result.data);
      }

      await loadMessages({ page: currentPage });
    } catch (error) {
      console.error('❌ Failed to update status:', error);
    }
  };

  const handlePriorityChange = async (messageId, newPriority) => {
    try {
      await dispatch(updateMessage({
        id: messageId,
        data: { priority: newPriority }
      })).unwrap();

      dispatch(updateLocalMessage({
        messageId,
        updateData: { priority: newPriority }
      }));

      if (selectedMessage && selectedMessage._id === messageId) {
        const result = await dispatch(getMessageById(messageId)).unwrap();
        setSelectedMessageState(result.data);
      }

      await loadMessages({ page: currentPage });
    } catch (error) {
      console.error('❌ Failed to update priority:', error);
    }
  };

  const handleResendNotification = async (messageId) => {
    try {
      await dispatch(resendNotification(messageId)).unwrap();
      // Show success toast or notification
    } catch (error) {
      console.error('❌ Failed to resend notification:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadMessages({ page: currentPage });
    await loadStatistics();
    setIsRefreshing(false);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedPriority('all');
    setSelectedType('all');
    dispatch(clearMessageFilters());
  };

  const handleExportCSV = () => {
    // Implement CSV export
    console.log('Exporting messages to CSV...');
  };

  const getStatusCount = (status) => {
    return messages.filter(m => m.status === status).length;
  };

  const getPriorityCount = (priority) => {
    return messages.filter(m => m.priority === priority).length;
  };

  // ============================================================
  // RENDER FUNCTIONS
  // ============================================================

  const renderMessageRow = (message) => {
    const isSelected = selectedMessageId === message._id;
    const statusConfig = STATUS_CONFIG[message.status] || STATUS_CONFIG.pending;
    const priorityConfig = PRIORITY_CONFIG[message.priority] || PRIORITY_CONFIG.medium;
    const typeConfig = MESSAGE_TYPE_CONFIG[message.messageType] || MESSAGE_TYPE_CONFIG.general;
    const StatusIcon = statusConfig.icon;
    const PriorityIcon = priorityConfig.icon;

    return (
      <tr 
        key={message._id}
        className={`${styles.messageRow} ${isSelected ? styles.selected : ''}`}
        onClick={() => handleMessageSelect(message)}
      >
        <td className={styles.messageCheckbox}>
          <input type="checkbox" />
        </td>
        <td className={styles.messageSubject}>
          <div className={styles.subjectWrapper}>
            <span className={styles.subjectText}>{message.subject}</span>
            {message.isEscalated && (
              <span className={styles.escalatedBadge}>
                <AlertTriangle size={12} />
                Escalated
              </span>
            )}
            {message.isOverdue && (
              <span className={styles.overdueBadge}>
                <Clock size={12} />
                Overdue
              </span>
            )}
          </div>
          <div className={styles.messageMeta}>
            <span className={styles.messageName}>
              <User size={12} />
              {message.name}
            </span>
            <span className={styles.messageEmail}>{message.email}</span>
          </div>
        </td>
        <td>
          <StatusBadge status={message.status} />
        </td>
        <td>
          <PriorityBadge priority={message.priority} />
        </td>
        <td>
          <MessageTypeBadge type={message.messageType} />
        </td>
        <td className={styles.messageDate}>
          <div className={styles.dateWrapper}>
            <span>{new Date(message.createdAt).toLocaleDateString()}</span>
            <span className={styles.timeSmall}>
              {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </td>
        <td>
          <div className={styles.messageActions}>
            <button 
              className={styles.actionBtn}
              onClick={(e) => { e.stopPropagation(); handleMessageSelect(message); }}
              title="View"
            >
              <Eye size={16} />
            </button>
            <button 
              className={styles.actionBtn}
              onClick={(e) => { e.stopPropagation(); setSelectedMessageState(message); setShowReplyModal(true); }}
              title="Reply"
            >
              <Reply size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderDetailPanel = () => {
    if (!selectedMessage) return null;

    const statusConfig = STATUS_CONFIG[selectedMessage.status] || STATUS_CONFIG.pending;
    const priorityConfig = PRIORITY_CONFIG[selectedMessage.priority] || PRIORITY_CONFIG.medium;
    const typeConfig = MESSAGE_TYPE_CONFIG[selectedMessage.messageType] || MESSAGE_TYPE_CONFIG.general;

    return (
      <div className={styles.detailPanel}>
        <div className={styles.detailHeader}>
          <div className={styles.detailTitle}>
            <h3>{selectedMessage.subject}</h3>
            <div className={styles.detailBadges}>
              <StatusBadge status={selectedMessage.status} />
              <PriorityBadge priority={selectedMessage.priority} />
              <MessageTypeBadge type={selectedMessage.messageType} />
              {selectedMessage.isEscalated && (
                <span className={styles.escalatedBadge}>
                  <AlertTriangle size={14} />
                  Escalated
                </span>
              )}
              {selectedMessage.isOverdue && (
                <span className={styles.overdueBadge}>
                  <Clock size={14} />
                  Overdue
                </span>
              )}
            </div>
          </div>
          <div className={styles.detailActions}>
            <button 
              className={styles.detailActionBtn}
              onClick={() => setShowReplyModal(true)}
            >
              <Reply size={18} />
              Reply
            </button>
            <button 
              className={styles.detailActionBtn}
              onClick={() => handleRefresh()}
            >
              <RefreshCw size={18} />
            </button>
            <button 
              className={styles.detailCloseBtn}
              onClick={() => setShowDetailPanel(false)}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className={styles.detailBody}>
          <div className={styles.detailSender}>
            <div className={styles.senderAvatar}>
              {selectedMessage.name?.charAt(0) || 'U'}
            </div>
            <div className={styles.senderInfo}>
              <div className={styles.senderName}>{selectedMessage.name}</div>
              <div className={styles.senderContact}>
                <span>{selectedMessage.email}</span>
                {selectedMessage.phone && (
                  <>
                    <span className={styles.senderDivider}>•</span>
                    <span><Phone size={14} /> {selectedMessage.phone}</span>
                  </>
                )}
              </div>
              <div className={styles.senderMeta}>
                <span><Calendar size={14} /> {new Date(selectedMessage.createdAt).toLocaleString()}</span>
                <span><ClockIcon size={14} /> Age: {selectedMessage.ageInHours?.toFixed(1)}h</span>
              </div>
            </div>
          </div>

          <div className={styles.detailContent}>
            <div className={styles.contentLabel}>Message</div>
            <div className={styles.contentBody}>
              {selectedMessage.content}
            </div>
          </div>

          {selectedMessage.responses && selectedMessage.responses.length > 0 && (
            <div className={styles.detailResponses}>
              <div className={styles.responsesHeader}>
                <span>Responses ({selectedMessage.responses.length})</span>
              </div>
              {selectedMessage.responses.map((response, index) => (
                <div key={index} className={styles.responseItem}>
                  <div className={styles.responseMeta}>
                    <span className={styles.responseSender}>
                      {response.sentBy === 'admin' ? 'Admin' : response.sentBy || 'System'}
                    </span>
                    <span className={styles.responseDate}>
                      {new Date(response.sentAt).toLocaleString()}
                    </span>
                  </div>
                  <div className={styles.responseContent}>
                    {response.content}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={styles.detailStatusControls}>
            <div className={styles.statusControlGroup}>
              <label>Change Status</label>
              <div className={styles.statusButtons}>
                {Object.keys(STATUS_CONFIG).map(status => (
                  <button
                    key={status}
                    className={`${styles.statusBtn} ${selectedMessage.status === status ? styles.active : ''}`}
                    onClick={() => handleStatusChange(selectedMessage._id, status)}
                    style={{
                      backgroundColor: selectedMessage.status === status ? STATUS_CONFIG[status].bg : 'transparent',
                      color: selectedMessage.status === status ? STATUS_CONFIG[status].color : '#6B7280',
                      borderColor: STATUS_CONFIG[status].color
                    }}
                  >
                    {STATUS_CONFIG[status].label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.statusControlGroup}>
              <label>Change Priority</label>
              <div className={styles.statusButtons}>
                {Object.keys(PRIORITY_CONFIG).map(priority => (
                  <button
                    key={priority}
                    className={`${styles.priorityBtn} ${selectedMessage.priority === priority ? styles.active : ''}`}
                    onClick={() => handlePriorityChange(selectedMessage._id, priority)}
                    style={{
                      backgroundColor: selectedMessage.priority === priority ? PRIORITY_CONFIG[priority].bg : 'transparent',
                      color: selectedMessage.priority === priority ? PRIORITY_CONFIG[priority].color : '#6B7280',
                      borderColor: PRIORITY_CONFIG[priority].color
                    }}
                  >
                    {PRIORITY_CONFIG[priority].label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStats = () => {
    return (
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#EFF6FF', color: '#3B82F6' }}>
            <Mail size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{totalMessages}</span>
            <span className={styles.statLabel}>Total Messages</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#FEF3C7', color: '#F59E0B' }}>
            <Clock size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{pendingMessages.length}</span>
            <span className={styles.statLabel}>Pending</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#D1FAE5', color: '#10B981' }}>
            <CheckCircle size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{resolvedMessages.length}</span>
            <span className={styles.statLabel}>Resolved</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#FEE2E2', color: '#EF4444' }}>
            <AlertTriangle size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{urgentCount}</span>
            <span className={styles.statLabel}>Urgent</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}>
            <AlertCircle size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{overdueCount}</span>
            <span className={styles.statLabel}>Overdue</span>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ backgroundColor: '#EDE9FE', color: '#8B5CF6' }}>
            <Flag size={20} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{escalatedCount}</span>
            <span className={styles.statLabel}>Escalated</span>
          </div>
        </div>
      </div>
    );
  };

  const renderFilters = () => {
    return (
      <div className={`${styles.filterSection} ${showFilters ? styles.expanded : ''}`}>
        <div className={styles.filterHeader}>
          <button 
            className={styles.filterToggle}
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            <span>Filters</span>
            <ChevronDown size={16} className={showFilters ? styles.rotated : ''} />
          </button>
          <button 
            className={styles.clearFiltersBtn}
            onClick={handleClearFilters}
          >
            <X size={14} />
            Clear All
          </button>
        </div>

        {showFilters && (
          <div className={styles.filterBody}>
            <div className={styles.filterGroup}>
              <label>Status</label>
              <div className={styles.filterOptions}>
                <button
                  className={`${styles.filterOption} ${selectedStatus === 'all' ? styles.active : ''}`}
                  onClick={() => setSelectedStatus('all')}
                >
                  All ({totalMessages})
                </button>
                {Object.keys(STATUS_CONFIG).map(status => (
                  <button
                    key={status}
                    className={`${styles.filterOption} ${selectedStatus === status ? styles.active : ''}`}
                    onClick={() => setSelectedStatus(status)}
                    style={{
                      borderColor: selectedStatus === status ? STATUS_CONFIG[status].color : 'transparent'
                    }}
                  >
                    <span style={{ color: STATUS_CONFIG[status].color }}>
                      {STATUS_CONFIG[status].label}
                    </span>
                    <span className={styles.filterCount}>{getStatusCount(status)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label>Priority</label>
              <div className={styles.filterOptions}>
                <button
                  className={`${styles.filterOption} ${selectedPriority === 'all' ? styles.active : ''}`}
                  onClick={() => setSelectedPriority('all')}
                >
                  All
                </button>
                {Object.keys(PRIORITY_CONFIG).map(priority => (
                  <button
                    key={priority}
                    className={`${styles.filterOption} ${selectedPriority === priority ? styles.active : ''}`}
                    onClick={() => setSelectedPriority(priority)}
                    style={{
                      borderColor: selectedPriority === priority ? PRIORITY_CONFIG[priority].color : 'transparent'
                    }}
                  >
                    <span style={{ color: PRIORITY_CONFIG[priority].color }}>
                      {PRIORITY_CONFIG[priority].label}
                    </span>
                    <span className={styles.filterCount}>{getPriorityCount(priority)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label>Message Type</label>
              <div className={styles.filterOptions}>
                <button
                  className={`${styles.filterOption} ${selectedType === 'all' ? styles.active : ''}`}
                  onClick={() => setSelectedType('all')}
                >
                  All
                </button>
                {Object.keys(MESSAGE_TYPE_CONFIG).map(type => (
                  <button
                    key={type}
                    className={`${styles.filterOption} ${selectedType === type ? styles.active : ''}`}
                    onClick={() => setSelectedType(type)}
                  >
                    <span style={{ color: MESSAGE_TYPE_CONFIG[type].color }}>
                      {MESSAGE_TYPE_CONFIG[type].label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPagination = () => {
    if (pagination.pages <= 1) return null;

    const pages = [];
    const totalPages = pagination.pages || 1;
    const current = pagination.page || 1;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= current - 2 && i <= current + 2)) {
        pages.push(i);
      } else if (i === current - 3 || i === current + 3) {
        pages.push('...');
      }
    }

    return (
      <div className={styles.pagination}>
        <div className={styles.paginationInfo}>
          Showing {(current - 1) * itemsPerPage + 1} to {Math.min(current * itemsPerPage, totalMessages)} of {totalMessages} messages
        </div>
        <div className={styles.paginationControls}>
          <button
            className={styles.paginationBtn}
            disabled={current <= 1}
            onClick={() => handlePageChange(current - 1)}
          >
            <ArrowLeft size={16} />
          </button>
          {pages.map((page, index) => (
            <button
              key={index}
              className={`${styles.pageBtn} ${page === current ? styles.active : ''}`}
              onClick={() => typeof page === 'number' && handlePageChange(page)}
              disabled={typeof page !== 'number'}
            >
              {page}
            </button>
          ))}
          <button
            className={styles.paginationBtn}
            disabled={current >= totalPages}
            onClick={() => handlePageChange(current + 1)}
          >
            <ArrowRight size={16} />
          </button>
        </div>
        <div className={styles.paginationLimit}>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>per page</span>
        </div>
      </div>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================
  return (
    <div className={styles.messagesPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>
            <Mail size={24} />
            Message Management
          </h1>
          <span className={styles.messageCount}>
            {totalMessages} total messages
          </span>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.exportBtn} onClick={handleExportCSV}>
            <Download size={18} />
            Export CSV
          </button>
          <button className={styles.refreshBtn} onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw size={18} className={isRefreshing ? styles.spinning : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      {showStats && renderStats()}

      {/* Search and Filters */}
      <div className={styles.searchSection}>
        <div className={styles.searchWrapper}>
          <Search size={18} className={styles.searchIcon} />
          <input
            ref={searchInputRef}
            type="text"
            className={styles.searchInput}
            placeholder="Search messages by name, email, subject, or content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <button 
          className={styles.toggleStatsBtn}
          onClick={() => setShowStats(!showStats)}
        >
          <BarChart3 size={18} />
          {showStats ? 'Hide Stats' : 'Show Stats'}
        </button>
      </div>

      {renderFilters()}

      {/* Messages Table */}
      <div className={styles.tableContainer}>
        <table className={styles.messageTable}>
          <thead>
            <tr>
              <th className={styles.checkboxCol}>
                <input type="checkbox" />
              </th>
              <th className={styles.subjectCol}>
                <button 
                  className={styles.sortableHeader}
                  onClick={() => handleSort('subject')}
                >
                  Subject
                  {sortField === 'subject' && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </button>
              </th>
              <th className={styles.statusCol}>
                <button 
                  className={styles.sortableHeader}
                  onClick={() => handleSort('status')}
                >
                  Status
                  {sortField === 'status' && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </button>
              </th>
              <th className={styles.priorityCol}>
                <button 
                  className={styles.sortableHeader}
                  onClick={() => handleSort('priority')}
                >
                  Priority
                  {sortField === 'priority' && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </button>
              </th>
              <th className={styles.typeCol}>Type</th>
              <th className={styles.dateCol}>
                <button 
                  className={styles.sortableHeader}
                  onClick={() => handleSort('createdAt')}
                >
                  Date
                  {sortField === 'createdAt' && (
                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </button>
              </th>
              <th className={styles.actionsCol}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className={styles.loadingCell}>
                  <Loader2 size={32} className={styles.spinning} />
                  <span>Loading messages...</span>
                </td>
              </tr>
            ) : messages.length === 0 ? (
              <tr>
                <td colSpan="7" className={styles.emptyCell}>
                  <Mail size={48} />
                  <h3>No messages found</h3>
                  <p>Try adjusting your search or filters</p>
                </td>
              </tr>
            ) : (
              messages.map(renderMessageRow)
            )}
          </tbody>
        </table>
      </div>

      {renderPagination()}

      {/* Detail Panel */}
      {showDetailPanel && renderDetailPanel()}

      {/* Reply Modal */}
      {showReplyModal && (
        <div className={styles.modalOverlay} onClick={() => setShowReplyModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                <Reply size={20} />
                Reply to Message
              </h3>
              <button className={styles.modalClose} onClick={() => setShowReplyModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.replyMessageInfo}>
                <div className={styles.replySubject}>Subject: {selectedMessage?.subject}</div>
                <div className={styles.replySender}>From: {selectedMessage?.name} ({selectedMessage?.email})</div>
              </div>
              <textarea
                ref={replyTextareaRef}
                className={styles.replyTextarea}
                placeholder="Type your reply here..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                rows={6}
              />
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.cancelBtn}
                onClick={() => setShowReplyModal(false)}
              >
                Cancel
              </button>
              <button 
                className={styles.sendBtn}
                onClick={handleReplySubmit}
                disabled={!replyContent.trim()}
              >
                <Send size={16} />
                Send Reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessagesPage;